import React, { useMemo } from "react";
import { Card, Button } from "react-bootstrap";

const WorkOrderUserAssignment = ({
  users = [],
  selectedProjectId = "",
  selectedUserIds = [],
  onChange,
  disabled = false,
}) => {
  // Normalize selected IDs as strings
  const selectedSet = useMemo(() => {
    return new Set((selectedUserIds || []).map((id) => String(id?._id || id)));
  }, [selectedUserIds]);

  // Find users assigned to the selected project ONLY
  const projectUsers = useMemo(() => {
    if (!selectedProjectId) return [];
    const pIdStr = String(selectedProjectId);
    return (users || []).filter((u) => {
      if (!u) return false;

      // 1. Check assignedProjects array (holds direct and/or team merged projects)
      const hasInAssignedProjects = (u.assignedProjects || []).some((p) => {
        if (!p) return false;
        const pid = typeof p === "object" ? (p._id || p.id) : p;
        return String(pid) === pIdStr;
      });
      if (hasInAssignedProjects) return true;

      // 2. Check directProjectsInfo
      const hasInDirect = (u.directProjectsInfo || []).some((p) => {
        if (!p) return false;
        const pid = typeof p === "object" ? (p._id || p.id) : p;
        return String(pid) === pIdStr;
      });
      if (hasInDirect) return true;

      // 3. Check teamProjectsInfo
      const hasInTeam = (u.teamProjectsInfo || []).some((p) => {
        if (!p) return false;
        const pid = typeof p === "object" ? (p._id || p.id) : p;
        return String(pid) === pIdStr;
      });
      if (hasInTeam) return true;

      return false;
    });
  }, [users, selectedProjectId]);

  // Toggle single user
  const handleToggleUser = (userId, forceAdd = false) => {
    if (disabled) return;
    const uidStr = String(userId);
    let next;
    if (selectedSet.has(uidStr) && !forceAdd) {
      next = (selectedUserIds || []).filter((id) => String(id?._id || id) !== uidStr);
    } else {
      if (selectedSet.has(uidStr)) return;
      next = [...(selectedUserIds || []), uidStr];
    }
    onChange?.(next);
  };

  // Select all project users
  const handleSelectAllProjectUsers = () => {
    if (disabled || projectUsers.length === 0) return;
    const allProjIds = projectUsers.map((u) => String(u._id));
    const merged = Array.from(new Set([...Array.from(selectedSet), ...allProjIds]));
    onChange?.(merged);
  };

  // Clear all assigned users
  const handleClearAll = () => {
    if (disabled) return;
    onChange?.([]);
  };

  // Remove a specific user
  const handleRemoveUser = (userId) => {
    if (disabled) return;
    const uidStr = String(userId);
    const next = (selectedUserIds || []).filter((id) => String(id?._id || id) !== uidStr);
    onChange?.(next);
  };

  return (
    <Card className="mb-3 border shadow-none wo-assignment-card">
      <Card.Body className="p-3">
        {/* Header */}
        <div className="d-flex flex-wrap justify-content-between align-items-center mb-2 gap-2">
          <div>
            <label className="form-label fw-bold mb-0" style={{ fontSize: "14px" }}>
              <i className="fa fa-users text-primary me-2"></i>
              Work Order User Assignment (Project Members Only)
            </label>
            <div className="text-muted small">
              Only members assigned to this project can be assigned to this Work Order.
            </div>
          </div>

          {selectedProjectId && projectUsers.length > 0 && !disabled && (
            <div className="d-flex gap-2">
              <Button
                type="button"
                variant="outline-primary"
                size="sm"
                className="py-1 px-2"
                style={{ fontSize: "12px" }}
                onClick={handleSelectAllProjectUsers}
              >
                <i className="fa fa-check-double me-1"></i> Select All Project Users ({projectUsers.length})
              </Button>
              {selectedSet.size > 0 && (
                <Button
                  type="button"
                  variant="outline-secondary"
                  size="sm"
                  className="py-1 px-2"
                  style={{ fontSize: "12px" }}
                  onClick={handleClearAll}
                >
                  <i className="fa fa-times me-1"></i> Clear
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Project Users Preview */}
        {!selectedProjectId ? (
          <div className="alert alert-light border py-2 px-3 mb-2 small text-muted d-flex align-items-center">
            <i className="fa fa-info-circle me-2 text-primary"></i>
            Please select a Project above to view and assign its linked team members.
          </div>
        ) : projectUsers.length === 0 ? (
          <div className="alert alert-warning py-2 px-3 mb-2 small d-flex align-items-center">
            <i className="fa fa-exclamation-triangle me-2"></i>
            No users are currently assigned to this project.
          </div>
        ) : (
          <div className="mb-3">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <span className="small text-muted fw-semibold">
                Project Members Preview (Click any member to toggle):
              </span>
              <span className="badge badge-light text-primary border" style={{ fontSize: "11px" }}>
                {projectUsers.filter((u) => selectedSet.has(String(u._id))).length} / {projectUsers.length} Selected
              </span>
            </div>

            <div className="d-flex flex-wrap gap-2">
              {projectUsers.map((u) => {
                const uidStr = String(u._id);
                const isSelected = selectedSet.has(uidStr);
                const roleName = u.role?.name || "Member";

                return (
                  <div
                    key={u._id}
                    onClick={() => handleToggleUser(u._id)}
                    className={`wo-user-pill ${isSelected ? "selected" : ""}`}
                    style={{
                      cursor: disabled ? "default" : "pointer",
                      padding: "6px 12px",
                      borderRadius: "6px",
                      transition: "all 0.15s ease",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      userSelect: "none",
                    }}
                    title={isSelected ? "Click to unassign" : "Click to assign to this Work Order"}
                  >
                    <i
                      className={`fa ${isSelected ? "fa-check-circle text-primary" : "fa-circle-o text-muted"
                        }`}
                      style={{ fontSize: "14px" }}
                    ></i>
                    <div>
                      <div
                        className="fw-semibold wo-user-name"
                        style={{ fontSize: "13px", lineHeight: 1.2 }}
                      >
                        {u.name}
                      </div>
                      <div className="text-muted" style={{ fontSize: "11px" }}>
                        {roleName}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Dropdown Selector - PROJECT USERS ONLY */}
        <div className="mb-2">
          <label className="form-label small text-muted mb-1">
            Select Project User via Dropdown:
          </label>
          <select
            className="form-select form-select-sm"
            value=""
            disabled={disabled || !selectedProjectId || projectUsers.length === 0}
            onChange={(e) => {
              if (e.target.value) {
                handleToggleUser(e.target.value, true);
              }
            }}
          >
            {!selectedProjectId ? (
              <option value="">-- Please select a Project first --</option>
            ) : projectUsers.length === 0 ? (
              <option value="">-- No users linked to this project --</option>
            ) : (
              <>
                <option value="">-- Choose project user to assign --</option>
                {projectUsers.map((u) => {
                  const isSelected = selectedSet.has(String(u._id));
                  return (
                    <option key={u._id} value={u._id}>
                      {isSelected ? "✓ [Selected] " : "+ "}{u.name} {u.role?.name ? `(${u.role.name})` : ""}
                    </option>
                  );
                })}
              </>
            )}
          </select>
        </div>

        {/* Selected Users Badges */}
        {selectedSet.size > 0 && (
          <div className="mt-2 pt-2 border-top">
            <div className="small text-muted fw-semibold mb-2">
              Assigned to this Work Order ({selectedSet.size}):
            </div>
            <div className="d-flex flex-wrap gap-2">
              {Array.from(selectedSet).map((uid) => {
                const u = (users || []).find((usr) => String(usr._id) === String(uid));
                if (!u) return null;
                const roleName = u.role?.name || "Member";

                return (
                  <span
                    key={uid}
                    className="badge bg-primary text-white d-inline-flex align-items-center py-1 px-2"
                    style={{ fontSize: "12px", gap: "6px" }}
                  >
                    <span>
                      {u.name} ({roleName})
                    </span>
                    {!disabled && (
                      <i
                        className="fa fa-times text-white cursor-pointer ms-1"
                        style={{ cursor: "pointer", opacity: 0.8 }}
                        title="Remove"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveUser(uid);
                        }}
                      ></i>
                    )}
                  </span>
                );
              })}
            </div>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default WorkOrderUserAssignment;
